import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, Upload, Trash2, Loader2, FileText, Image as ImageIcon, Eye, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const openReceipt = async (txnId, r) => {
  try {
    const res = await axios.get(`${API}/transactions/${txnId}/receipts/${r.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error("Could not open receipt"); }
};

export const ReceiptsDialog = ({ txn, onClose, onChanged }) => {
  const [receipts, setReceipts] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  useEffect(() => { setReceipts(txn?.receipts || []); }, [txn]);

  const upload = async (files) => {
    setBusy(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        added.push((await axios.post(`${API}/transactions/${txn.id}/receipts`, fd)).data);
      }
      setReceipts(r => [...r, ...added]);
      toast.success(`${added.length} receipt${added.length > 1 ? "s" : ""} attached`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (r) => {
    setBusy(true);
    try { await axios.delete(`${API}/transactions/${txn.id}/receipts/${r.id}`); setReceipts(x => x.filter(y => y.id !== r.id)); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove receipt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!txn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="receipts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Paperclip className="w-4 h-4" /> Receipts</DialogTitle>
          <DialogDescription>{txn && `${txn.date} · ${txn.vendor || txn.description || txn.category} · $${parseFloat(txn.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {receipts.length === 0 && <p className="text-sm text-stone-400 text-center py-4" data-testid="receipts-empty">No receipts attached</p>}
          {receipts.map(r => (
            <div key={r.id} className="flex items-center gap-3 border border-stone-100 rounded-lg px-3 py-2 text-sm" data-testid={`receipt-${r.id}`}>
              {r.content_type === "application/pdf" ? <FileText className="w-4 h-4 text-red-500" /> : <ImageIcon className="w-4 h-4 text-sky-500" />}
              <div className="min-w-0 flex-1">
                <p className="truncate text-stone-800">{r.original_name}</p>
                <p className="text-[11px] text-stone-400">{(r.size / 1024).toFixed(0)} KB · {r.uploaded_by}{r.source === "telegram" && !String(r.uploaded_by).includes("Telegram") ? " via Telegram" : ""} · {new Date(r.uploaded_at).toLocaleDateString()}</p>
              </div>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => txn && openReceipt(txn.id, r)} data-testid={`receipt-view-${r.id}`}><Eye className="w-3.5 h-3.5" /></Button>
              {txn && !txn.locked && <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50" disabled={busy} onClick={() => remove(r)} data-testid={`receipt-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          ))}
        </div>
        {txn?.locked ? (
          <p className="text-xs text-emerald-800 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> This month is signed off. Receipts are read-only.</p>
        ) : (
          <>
            <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" className="hidden" data-testid="receipt-file-input"
              onChange={e => { if (e.target.files.length) upload([...e.target.files]); e.target.value = ""; }} />
            <Button variant="outline" className="w-full border-dashed" disabled={busy} onClick={() => input.current?.click()} data-testid="receipt-upload-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Attach receipt (image or PDF, max 10MB)
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
